"use client";

import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { clearPortalCache } from "@/lib/core-query";
import { useCoreAuth } from "@/lib/core-auth";
import {
  bindClientesSelection,
  bindWorkspaceSelection,
  contextsQueryKey,
  ContextsRequestError,
  fetchOperationalContexts,
  parseClientesParam,
  preserveOperationalContextHref,
  serializeClientesParam,
  type ContextsDocument,
  type OperationalContext,
  type WorkspaceContext,
} from "@/lib/contexts";

type OperationalContextValue = {
  document: ContextsDocument | null;
  items: OperationalContext[];
  selected: OperationalContext | null;
  selectedWorkspace: WorkspaceContext | null;
  selectedWorkspaceId: string;
  selectedIds: string[];
  allSelected: boolean;
  writeEnabled: boolean;
  writeHint: string | null;
  setSelectedIds: (ids: string[]) => void;
  setSelectedId: (id: string) => void;
  setSelectedWorkspaceId: (id: string) => void;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const OperationalContextContext = createContext<OperationalContextValue | null>(null);

function OperationalContextInner({ children }: { children: ReactNode }) {
  const { user, clear } = useCoreAuth();
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const clientesRaw = searchParams.get("clientes");
  const selectedIds = useMemo(() => parseClientesParam(clientesRaw), [clientesRaw]);
  const selectedWorkspaceId = searchParams.get("workspace_id") ?? "";
  const unitId = user?.business_unit_id ?? null;

  const { data, error, isError, isPending, refetch } = useQuery({
    queryKey: contextsQueryKey(user?.id, unitId, `${clientesRaw ?? ""}::${selectedWorkspaceId}`),
    enabled: Boolean(user),
    queryFn: ({ signal }) => fetchOperationalContexts(signal, selectedWorkspaceId || null),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!(error instanceof ContextsRequestError)) return;
    if (error.status === 401 || error.status === 403) {
      void clearPortalCache(queryClient).then(() => {
        if (error.status === 401) {
          clear();
          router.push("/entrar");
        } else if (selectedWorkspaceId) {
          const params = new URLSearchParams(searchParams.toString());
          params.delete("workspace_id");
          const query = params.toString();
          router.replace(query ? `${pathname}?${query}` : pathname);
        }
      });
    }
  }, [error, clear, router, queryClient, selectedWorkspaceId, searchParams, pathname]);

  const items = useMemo(() => data?.items ?? [], [data]);
  const kind = data?.kind ?? "platform";
  const allSelected = selectedIds.length === 0 && kind !== "customer";

  useEffect(() => {
    bindClientesSelection(selectedIds.length ? selectedIds : kind === "customer" ? [] : []);
    return () => bindClientesSelection(null);
  }, [selectedIds, kind]);

  useEffect(() => {
    bindWorkspaceSelection(selectedWorkspaceId || null);
    return () => bindWorkspaceSelection(null);
  }, [selectedWorkspaceId]);

  const selected = useMemo(() => {
    if (selectedIds.length !== 1) return null;
    if (kind === "customer") {
      return items.find((item) => item.tenant_id === selectedIds[0]) ?? null;
    }
    return items.find((item) => item.id === selectedIds[0]) ?? null;
  }, [items, selectedIds, kind]);
  const selectedWorkspace = useMemo(
    () => (data?.workspaces ?? []).find((workspace) => workspace.id === selectedWorkspaceId) ?? null,
    [data?.workspaces, selectedWorkspaceId],
  );

  const writeEnabled = selectedIds.length === 1;
  const writeHint = writeEnabled ? null : "Selecione um único cliente";

  const replaceClientes = useCallback(
    (ids: string[]) => {
      const params = new URLSearchParams(searchParams.toString());
      const value = serializeClientesParam(ids);
      if (!value) params.delete("clientes");
      else params.set("clientes", value);
      if (serializeClientesParam(selectedIds) !== value) params.delete("workspace_id");
      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : pathname);
    },
    [pathname, router, searchParams, selectedIds],
  );

  const setSelectedIds = useCallback(
    (ids: string[]) => {
      const allowed =
        kind === "customer"
          ? new Set((data?.tenants ?? []).map((item) => item.id))
          : new Set(items.map((item) => item.id));
      if (ids.some((id) => !allowed.has(id))) {
        return;
      }
      replaceClientes(ids);
    },
    [data?.tenants, items, kind, replaceClientes],
  );

  const setSelectedId = useCallback(
    (id: string) => {
      setSelectedIds(id ? [id] : []);
    },
    [setSelectedIds],
  );

  const setSelectedWorkspaceId = useCallback(
    (id: string) => {
      const allowed = new Set((data?.workspaces ?? []).map((workspace) => workspace.id));
      if (id && !allowed.has(id)) return;
      const params = new URLSearchParams(searchParams.toString());
      if (id) params.set("workspace_id", id);
      else params.delete("workspace_id");
      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : pathname);
    },
    [data?.workspaces, pathname, router, searchParams],
  );

  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const value = useMemo<OperationalContextValue>(
    () => ({
      document: data ?? null,
      items,
      selected,
      selectedWorkspace,
      selectedWorkspaceId,
      selectedIds,
      allSelected,
      writeEnabled,
      writeHint,
      setSelectedIds,
      setSelectedId,
      setSelectedWorkspaceId,
      loading: Boolean(user) && isPending,
      error:
        isError && !(error instanceof ContextsRequestError && (error.status === 401 || error.status === 403))
          ? error instanceof Error
            ? error.message
            : "Não foi possível carregar os contextos"
          : null,
      refresh,
    }),
    [
      data,
      items,
      selected,
      selectedWorkspace,
      selectedWorkspaceId,
      selectedIds,
      allSelected,
      writeEnabled,
      writeHint,
      setSelectedIds,
      setSelectedId,
      setSelectedWorkspaceId,
      user,
      isPending,
      isError,
      error,
      refresh,
    ],
  );

  return (
    <OperationalContextContext.Provider value={value}>{children}</OperationalContextContext.Provider>
  );
}

const prerenderValue: OperationalContextValue = {
  document: null,
  items: [],
  selected: null,
  selectedWorkspace: null,
  selectedWorkspaceId: "",
  selectedIds: [],
  allSelected: true,
  writeEnabled: false,
  writeHint: null,
  setSelectedIds: () => {},
  setSelectedId: () => {},
  setSelectedWorkspaceId: () => {},
  loading: true,
  error: null,
  refresh: async () => {},
};

export function OperationalContextProvider({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <OperationalContextContext.Provider value={prerenderValue}>
          {children}
        </OperationalContextContext.Provider>
      }
    >
      <OperationalContextInner>{children}</OperationalContextInner>
    </Suspense>
  );
}

export function useOperationalContext(): OperationalContextValue {
  const ctx = useContext(OperationalContextContext);
  if (!ctx) {
    throw new Error("OperationalContextProvider ausente");
  }
  return ctx;
}

/** Link helper shared by the shell and pages to retain URL context. */
export function useContextualHref() {
  const searchParams = useSearchParams();
  const currentSearch = searchParams.toString();
  return useCallback(
    (href: string) => preserveOperationalContextHref(href, currentSearch),
    [currentSearch],
  );
}
